/**
 * @fileoverview CollapsibleCard — shared chrome for the overview cards.
 * @module components/jobs/phases/overview/CollapsibleCard
 *
 * The job overview page renders a stack of named cards (Background, Details,
 * Appointment Details, Team, …). Each shares the same shadow chrome
 * and an icon + title header. This helper wraps them in a Radix Collapsible
 * so the user can hide content they don't need, exposes a `statusSlot` for
 * inline status pills, and an `actions` slot for header-row buttons that
 * shouldn't toggle the collapse state.
 */

import { type ComponentType } from "react";
import { ChevronDown } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import { cn } from "@/utils/misc";
import { usePersistedDisclosure } from "@/utils/use-persisted-disclosure";

const CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

interface CollapsibleCardProps {
  icon: ComponentType<{ className?: string }>;
  title: string;
  /** Optional small pill rendered between the title and the actions. */
  statusSlot?: React.ReactNode;
  /** Buttons rendered on the right of the header. They are wrapped so clicks
   * don't toggle the card's collapsed state. */
  actions?: React.ReactNode;
  defaultOpen?: boolean;
  /**
   * Stable id for remembering this card's open/closed state per user across
   * sessions (localStorage). Omit to keep the card's state ephemeral.
   */
  persistId?: string;
  children: React.ReactNode;
  className?: string;
}

export function CollapsibleCard({
  icon: Icon,
  title,
  statusSlot,
  actions,
  defaultOpen = true,
  persistId,
  children,
  className,
}: CollapsibleCardProps) {
  const [open, setOpen] = usePersistedDisclosure(persistId, defaultOpen);

  return (
    <div className={cn(CARD_CLASS, className)}>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2.5 px-5 py-3 text-left transition-colors hover:bg-muted/30"
            aria-expanded={open}
          >
            <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <h4 className="min-w-0 flex-1 truncate text-sm font-semibold">
              {title}
            </h4>
            {statusSlot}
            {actions}
            <ChevronDown
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform duration-200",
                open ? "rotate-0" : "-rotate-90",
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t border-border">{children}</div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
